import { useEffect, useRef, useState } from "react";
import CustomText from "../atomic/CustomText";
import ImageUploader from "./ImageUploader";
import Button from "../organism/Button";
import FrontsDropDownArrow from "../../assets/svg/FontDropDownArrow";
import useSendForApproval from "../../services/utils/custom hooks/Approvals/useSendForApproval";
import moment from "moment";
import { useOktaAuth } from "@okta/okta-react";
import useMobile from "../../utils/helpers/useMobile";

function FontsDropDown({
  setDropdown,
  dropdown,
  selectedFont,
  setFontFamily,
  fontSize,
}: any) {
  const dropDownRef = useRef<any>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const condition =
        dropDownRef?.current && !dropDownRef?.current?.contains(event.target as Node);
      if (condition) {
        setDropdown(false);
      }
    };

    if (dropdown) {
      window.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdown, setDropdown]);

  return (
    <div className="relative inline-block text-left z-[2000]" ref={dropDownRef}>
      <div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setDropdown((prevState: boolean) => !prevState);
          }}
          type="button"
          className="inline-flex items-center w-full px-3 py-2 text-sm font-semibold text-primary uppercase"
          id="menu-button"
        >
          Change Style
          <FrontsDropDownArrow
            className={`mx-3 transition-transform ${dropdown ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {dropdown && (
        <div
          className="absolute right-0 top-8 z-[2001] w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          role="menu"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1" role="none">
            {["Dancing Script", "Birthstone", "Ephesis", "Great Vibes"].map(
              (font) => (
                <button
                  key={font}
                  onClick={() => {
                    setFontFamily(font);
                    setDropdown(false);
                  }} // Set selected font
                  className={`block px-4 py-2 w-full text-left text-gray-700 hover:bg-sideMenuHoverBg ${fontSize} ${
                    selectedFont === font ? "bg-sideMenuHoverBg" : ""
                  }`}
                  style={{ fontFamily: font, fontWeight: 700 }} // Preview font style in dropdown
                >
                  {font}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CustomTextSignature({
  fontFamily,
  setFontFamily,
  text,
  setText,
}: {
  readonly fontFamily: string;
  readonly setFontFamily: (val: string) => void;
  readonly text: string;
  readonly setText: (val: string) => void;
}) {
  const [dropDownOpen, setDropDownOpen] = useState<boolean>(false);
  const fontSize = "text-xl";

  return (
    <div className="bg-transparent flex flex-col items-end text-black absolute z-[999] w-[97%] text-md">
      <input
        className={`border-b-2 bg-transparent w-full px-4 pb-2 outline-none ${fontSize} text-black `}
        style={{ fontWeight: 400, fontFamily: fontFamily, color: '#000000' }}
        placeholder="Type Here"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <FontsDropDown
        dropdown={dropDownOpen}
        setDropdown={setDropDownOpen}
        selectedFont={fontFamily}
        setFontFamily={setFontFamily}
        fontSize={fontSize}
      />
    </div>
  );
}

function CustomSignatureInput({
  handleSubmission,
  submitting,
  children,
}: {
  handleSubmission: (signature: string) => void;
  submitting: boolean;
  children?: any;
}) {
  const canvasRef = useRef<any>(null);
  const isMobile = useMobile()
  const contextRef = useRef<any>(null);
  const [drawing, setDrawing] = useState<boolean>(false);
  const [drawingPresent, setDrawingPreset] = useState<boolean>(false);
  const [mode, setMode] = useState<string>("Type");
  const [fontFamily, setFontFamily] = useState<string>("Dancing Script");
  const [text, setText] = useState<string>("");
  const [image, setImage] = useState<null | File>(null);
  const textMissing = mode === "Type" && text === "";
  const imageMissing = mode === "Upload" && image === null;
  const drawingMissing = mode === "Draw" && drawingPresent === false;

  const [generatedTextSignature, setGeneratedTextSignature] = useState<boolean>(false);
  // Standard signature dimensions with consistent aspect ratio (approximately 4:1)
  const canvasWidth = 600;
  const canvasHeight = 150; // Consistent height for both Type and Draw modes

  // Canvas setup
  useEffect(() => {
    if (mode !== "Upload" && canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      
      // Set actual canvas dimensions - maintain consistent aspect ratio
      const width = isMobile ? 350 : canvasWidth;
      const height = canvasHeight;
      
      canvas.width = width;
      canvas.height = height;
      
      // Clear canvas and set white background
      context.fillStyle = "#F5F5F5"; // sideNavGray color
      context.fillRect(0, 0, width, height);
      
      // Setup drawing context
      context.strokeStyle = "black";
      context.lineWidth = 2;
      context.lineCap = "round";
      context.lineJoin = "round";
      contextRef.current = context;
    }
  }, [mode, isMobile]);

  // Improved coordinate calculation for mobile
  const getCanvasRelativeCoords = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { offsetX: 0, offsetY: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      offsetX: (clientX - rect.left) * scaleX,
      offsetY: (clientY - rect.top) * scaleY
    };
  };

  // Prevent default touch behavior
  useEffect(() => {
    const preventDefault = (e: any) => {
      if (e.target === canvasRef.current) {
        e.preventDefault();
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      // touch event listeners to prevent default behaviors
      canvas.addEventListener('touchstart', preventDefault, { passive: false });
      canvas.addEventListener('touchmove', preventDefault, { passive: false });
      canvas.addEventListener('touchend', preventDefault, { passive: false });
      
      return () => {
        canvas.removeEventListener('touchstart', preventDefault);
        canvas.removeEventListener('touchmove', preventDefault);
        canvas.removeEventListener('touchend', preventDefault);
      };
    }
  }, []);

  const startDrawing = (e: any) => {
    // Prevent all default behaviors
    e.preventDefault();
    e.stopPropagation();
    
    const context = contextRef.current;
    if (!context) return;
    
    let clientX, clientY;
    
    if (e.type === 'mousedown') {
      clientX = e.clientX;
      clientY = e.clientY;
    } else if (e.type === 'touchstart' && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      return;
    }
    
    const coords = getCanvasRelativeCoords(clientX, clientY);
    
    context.beginPath();
    context.moveTo(coords.offsetX, coords.offsetY);
    setDrawing(true);
  };

  const draw = (e: any) => {
    if (!drawing) return;
    
    // Prevent all default behaviors
    e.preventDefault();
    e.stopPropagation();
    
    const context = contextRef.current;
    if (!context) return;
    
    let clientX, clientY;
    
    if (e.type === 'mousemove') {
      clientX = e.clientX;
      clientY = e.clientY;
    } else if (e.type === 'touchmove' && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      return;
    }
    
    const coords = getCanvasRelativeCoords(clientX, clientY);
    
    context.lineTo(coords.offsetX, coords.offsetY);
    context.stroke();
  };

  const stopDrawing = () => {
    const context = contextRef.current;
    if (context) {
      context.closePath();
    }
    setDrawing(false);
    setDrawingPreset(true);
  };

  const cancelDrawing = () => {
    setDrawing(false);
    const context = contextRef.current;
    if (context) {
      context.closePath();
    }
  };

  const Tab = ({
    title,
    onClick,
    isActive,
  }: {
    title: string;
    onClick: any;
    isActive: boolean;
  }) => {
    return (
      <button
        className={`pr-5 pb-4 ${
          isActive ? "border-primary border-b-4 " : "pb-5"
        }`}
        onClick={onClick}
      >
        <CustomText
          text={title}
          fontWeight="font-semibold"
          className="!text-left text-sm"
        />
      </button>
    );
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext?.("2d");
    if (context && canvas) {
      // Clear and reset background
      context.fillStyle = "#F5F5F5";
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    setGeneratedTextSignature(false);
    setDrawingPreset(false);
    setText("");
  };

  const Tabs = () => (
    <div className="w-full flex justify-left border-b border-roundedInputBorder">
      {[
        {
          title: "Type",
          onClick: () => {
            clear();
            setMode("Type");
            // Reset drawing state when switching to Type
            setDrawingPreset(false);
          },
        },
        {
          title: "Draw",
          onClick: () => {
            clear();
            setMode("Draw");
            // Reset text signature state when switching to Draw
            setGeneratedTextSignature(false);
          },
        },
      ].map((tab, index) => (
        <Tab key={index} {...tab} isActive={mode === tab.title} />
      ))}
    </div>
  );

  async function handleSubmit() {
    if (mode === "Type" || mode === "Draw") {
      const canvas = canvasRef.current;
      const context = canvas?.getContext?.("2d");
      
      if (context && mode === "Type") {
        // Clear and set background before drawing text
        context.fillStyle = "#F5F5F5";
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        // Set text properties for better visibility
        context.fillStyle = "#000000"; // Pure black for maximum contrast
        context.font = `bold 24px ${fontFamily}`; // Increased size and made bold
        context.textAlign = "center";
        context.textBaseline = "middle";
        
        // Add text shadow for better visibility (optional, can be removed if not needed)
        context.shadowColor = "rgba(0, 0, 0, 0.1)";
        context.shadowBlur = 1;
        context.shadowOffsetX = 0;
        context.shadowOffsetY = 0;
        
        // Center text horizontally and vertically
        const x = canvas.width / 2;
        const y = canvas.height / 2;
        context.fillText(text, x, y);
        
        // Reset shadow
        context.shadowColor = "transparent";
        context.shadowBlur = 0;
        
        setGeneratedTextSignature(true);
      }
      
      // Export with consistent dimensions and proper quality
      handleSubmission(canvas.toDataURL("image/png", 1.0));
    }
  }

  return (
    <div
      className={`rounded-md flex justify-between flex-col w-full md:min-w-[350px] md:min-w-[${canvasWidth}px]`}
    >
      {children}
      <Tabs />
      <div className="my-5 relative justify-center flex items-center">
        {mode !== "Upload" && (
          <canvas
            ref={canvasRef}
            // Mouse events
            onMouseDown={startDrawing}
            onMouseUp={stopDrawing}
            onMouseMove={mode === "Draw" ? draw : undefined}
            onMouseLeave={stopDrawing}
            // Touch events
            onTouchStart={startDrawing}
            onTouchEnd={stopDrawing}
            onTouchMove={mode === "Draw" ? draw : undefined}
            onTouchCancel={cancelDrawing}
            // Styles - ensure display size matches actual canvas dimensions
            className="border cursor-pointer !bg-sideNavGray rounded-md touch-none"
            height={canvasHeight}
            width={isMobile ? 350 : canvasWidth}
            style={{
              // width: isMobile ? '350px' : `${canvasWidth}px`,
              width: isMobile ? 'min(100%, 350px)' : `${canvasWidth}px`,
              height: `${canvasHeight}px`,
              touchAction: 'none', // prevents browser touch handling
              display: 'block' // prevents inline spacing issues
            }}
          />
        )}
        {/* Rest of your JSX remains the same */}
        {mode === "Type" && generatedTextSignature === false && (
          <CustomTextSignature
            text={text}
            fontFamily={fontFamily}
            setFontFamily={setFontFamily}
            setText={setText}
          />
        )}
      </div>
      <div className="flex items-center mb-5">
        <CustomText
          text={"Note:"}
          fontWeight="font-semibold"
          className="text-md text-requiredRed mr-1 text-sm"
        />
        <CustomText
          text={"This signature will be used for future authorizations"}
          className="text-sm"
          fontWeight="font-normal"
        />
      </div>
      <div className="self-end flex">
        <Button
          buttonTheme="secondary"
          onClick={clear}
          disabled={textMissing || drawingMissing || submitting}
          className="mx-2"
          textInformation={{ text: "Clear", fontWeight: "font-semibold" }}
        />
        <Button
          buttonTheme="primary"
          disabled={textMissing || drawingMissing || submitting}
          loading={submitting}
          onClick={handleSubmit}
          textInformation={{ text: "Submit", fontWeight: "font-semibold" }}
        />
      </div>
    </div>
  );
}

export default CustomSignatureInput;
